// src/Pages/TemplateSettings/TemplateSettings.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, Grid, Paper, Divider, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ChromePicker } from 'react-color';
import { setAllTheme } from '../../reduxStore/themeReducer';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import settingAPI from "../../Network/Setting";
import CheckIcon from '@mui/icons-material/Check';
import { setGlobalLoading } from '../../reduxStore/SettingsReducer';
import Cookies from "universal-cookie";
const cookies = new Cookies();

const TemplateSettings = () => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const maintheme = useSelector(state => state.themeData.maintheme);
    const themeData = useSelector(state => state.themeData);
    const themeImages = themeData.images || {};
    // Default image paths
    const defaultImages = {
        company_one: themeImages.companyOne || '',
        company_two: themeImages.companyTwo || '',
        sidebar: themeImages.sidebar || '',
        main_icon: themeImages.mainIcon || '',
        loading: themeImages.loading || '',
        icon: themeImages.favicon || '',
        // companyOne: process.env.REACT_APP_API_URL_image + "public_images/company_one.png",
        // companyTwo: process.env.REACT_APP_API_URL_image + "public_images/company_two.png",
        // sidebar: process.env.REACT_APP_API_URL_image + "public_images/sidebar_logo.png",
        // mainIcon: process.env.REACT_APP_API_URL_image + "public_images/main_logo.png",
        // loading: process.env.REACT_APP_API_URL_image + "public_images/loading.png",
        // favicon: process.env.REACT_APP_API_URL_image + "public_images/icon.png" // Using main_logo for favicon as per your example
    };
    const [initialTheme, setInitialTheme] = useState(null);

    useEffect(() => {
        // Store the initial theme when component mounts
        setInitialTheme(JSON.parse(JSON.stringify(themeData)));
    }, []);

    const [settings, setSettings] = useState({
        ...defaultImages,
        primaryColor: maintheme?.primary || '#74bdcb',  // Fallback to default color
        secondaryColor: maintheme?.secondary || '#e72a56',  // Fallback to default color,
        appName: themeData?.app_name
    });

    const [showColorPicker, setShowColorPicker] = useState({
        primary: false,
        secondary: false
    });
    const handleImageUpload = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check if the file is a PNG
        if (file.type !== 'image/png') {
            toast.error(t('ERRORS.only_png_allowed'));
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setSettings(prev => ({
                ...prev,
                [type]: event.target.result,
                [`${type}_file`]: file
            }));
        };
        reader.onerror = () => {
            toast.error(t('ERRORS.file_upload_error'));
        };
        reader.readAsDataURL(file);
    };
    const handleImageReset = (type) => {
        if (initialTheme) {
            setSettings(prev => ({
                ...prev,
                [type]: initialTheme.images?.[type] || '',
                [`${type}_file`]: null
            }));
        }
    };
    // Rest of your component code remains the same...


    const handleColorChange = (color, type) => {
        setSettings(prev => ({
            ...prev,
            [type]: color.hex
        }));
    };
    const handleApplyTheme = () => {
        const primaryColor = settings.primaryColor;
        const secondaryColor = settings.secondaryColor;

        // Create a deep copy of the current theme
        const updatedTheme = JSON.parse(JSON.stringify(themeData));

        updatedTheme.app_name = settings.appName;
        // Update theme colors
        updatedTheme.maintheme.primary = primaryColor;
        updatedTheme.maintheme.iconColor = primaryColor;
        updatedTheme.maintheme.secondary = secondaryColor;
        updatedTheme.maintheme.secondaryColor = secondaryColor;

        // Update other theme colors (keep your existing color updates)
        updatedTheme.logintheme.platte_background_color = primaryColor;
        updatedTheme.logintheme.iconsColor = primaryColor;
        updatedTheme.logintheme.secondaryColor = secondaryColor;

        updatedTheme.sidemenutheme.secondaryColor = primaryColor;
        updatedTheme.sidemenutheme.itmeshover = `${primaryColor}29`;

        updatedTheme.dashboardtheme.background = primaryColor;
        updatedTheme.gridtheme.gridHeader = primaryColor;
        updatedTheme.gridtheme.tooltip_create = primaryColor;
        updatedTheme.appbar.background = primaryColor;
        updatedTheme.appbar.secondaryColor = secondaryColor;

        // Update images
        updatedTheme.images = {
            companyOne: settings.company_one,
            companyTwo: settings.company_two,
            sidebar: settings.sidebar,
            mainIcon: settings.main_icon,
            loading: settings.loading,
            favicon: settings.icon
        };


        const updateFavicon = (url) => {
            let favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
            favicon.rel = 'icon';
            favicon.href = url;
            document.getElementsByTagName('head')[0].appendChild(favicon);
        };

        // If favicon is being updated
        if (settings.icon) {
            updateFavicon(settings.icon);
        }
        // Apply the updated theme
        dispatch(setAllTheme(updatedTheme));

        // Update CSS variables
        console.log("settings.appName", settings.appName);
        document.title = settings.appName;
        document.documentElement.style.setProperty('--primary-color', primaryColor);
        document.documentElement.style.setProperty('--primary-color-light', primaryColor + "A0");
        document.documentElement.style.setProperty('--secondary-color', secondaryColor);
        document.documentElement.style.setProperty('--secondary-color-light', secondaryColor + "A0");

        toast.success(t('SETTINGS.theme_applied'));
    };
    const handleResetAll = () => {
        if (initialTheme) {
            // Reset colors
            setSettings(prev => ({
                ...prev,
                primaryColor: initialTheme.maintheme.primary,
                secondaryColor: initialTheme.maintheme.secondary,
                // Reset images to initial values
                company_one: initialTheme.images?.companyOne || '',
                company_two: initialTheme.images?.companyTwo || '',
                sidebar: initialTheme.images?.sidebar || '',
                main_icon: initialTheme.images?.mainIcon || '',
                loading: initialTheme.images?.loading || '',
                icon: initialTheme.images?.favicon || '',
                appName: t('GLOBAL.app_name')
            }));

            // Reset theme to initial state
            dispatch(setAllTheme(initialTheme));

            // Update CSS variables
            document.title = t('GLOBAL.app_name');
            document.documentElement.style.setProperty('--primary-color', initialTheme.maintheme.primary);
            document.documentElement.style.setProperty('--primary-color-light', initialTheme.maintheme.primary + "A0");
            document.documentElement.style.setProperty('--secondary-color', initialTheme.maintheme.secondary);
            document.documentElement.style.setProperty('--secondary-color-light', initialTheme.maintheme.secondary + "A0");

            // Reset favicon
            if (initialTheme.images?.favicon) {
                const favicon = document.querySelector("link[rel*='icon']");
                if (favicon) {
                    favicon.href = initialTheme.images.favicon;
                }
            }
        }
    };
    const saveThemeSettings = async () => {

        if (
            cookies.get("active_session") &&
            cookies.get("active_session") == "yes"
        ) {
            try {

                let params = {
                    username: cookies.get("user_name"),
                    user_id: cookies.get("user_id"),
                    account_name: cookies.get("account_name"),
                    account_id: cookies.get("account_id"),
                    sid: cookies.get("sid"),
                    baseurl: cookies.get("baseUrl"),
                    id: cookies.get("id"),
                };
                const formData = new FormData();

                // Add user_id for the backend to use in file naming

                // Add app name and colors
                formData.append('app_name', settings.appName);
                formData.append('primary_color', settings.primaryColor);
                formData.append('secondary_color', settings.secondaryColor);

                // Handle file uploads
                const imageFields = [
                    'company_one', 'company_two', 'sidebar',
                    'main_icon', 'loading', 'icon'
                ];

                // Add files to formData
                let hasFilesToUpload = false;
                imageFields.forEach(field => {
                    if (settings[field + "_file"] instanceof File) {
                        // Append image type as metadata with the file
                        formData.append('file_types', field);  // Add this line
                        formData.append('files', settings[field + "_file"]);
                        hasFilesToUpload = true;
                    } else if (typeof settings[field] === 'string') {
                        formData.append(`${field}_url`, settings[field]);
                    }
                });



                // Then update the settings with the file names if needed
                const result = await settingAPI.updateSettings({
                    data: formData,
                    params: params,
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                    },
                });

                if (result?.status && result?.data?.status) {
                    toast.success(t('SETTINGS.saved_successfully'));
                    if (result.data.theme) {
                        dispatch(setAllTheme(result.data.theme));
                    }
                } else {
                    throw new Error(result?.data?.message || t('COMMON.error_occurred'));
                }
            } catch (error) {
                console.error('Error saving theme settings:', error);
                toast.error(error.message || t('COMMON.error_occurred'));
            } finally {
                dispatch(setGlobalLoading(false));
            }
        }
    };

    // Update your handleSave function to use saveThemeSettings
    const handleSave = async () => {
        await saveThemeSettings();
    };

    const renderImageUpload = (label, type) => (
        <Grid item xs={12} md={6} lg={4} key={type}>
            <Paper elevation={3} sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: "Cairo" }}>
                    {t(`SETTINGS.${type}`)}
                </Typography>
                <Box
                    component="img"
                    src={settings[type] || themeImages[type]}
                    alt={label}
                    sx={{
                        width: '100%',
                        maxHeight: 150,
                        objectFit: 'contain !important',
                        mb: 2,
                        border: '1px solid #ddd',
                        borderRadius: 1,
                        p: 1
                    }}
                />
                <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    startIcon={<CloudUploadIcon />}
                    sx={{ fontFamily: "Cairo" }}
                >
                    {t('COMMON.upload')}
                    <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, type)}
                    />
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    onClick={() => handleImageReset(type)}
                    sx={{ fontFamily: "Cairo", mt: 1 }}
                >
                    {t('COMMON.reset')}
                </Button>
            </Paper>
        </Grid>
    );

    const renderColorPicker = (type) => (
        <Grid item xs={12} md={6} key={type}>
            <Paper elevation={3} sx={{ p: 2, position: 'relative' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: "Cairo" }}>
                    {t(`SETTINGS.${type}_color`)}
                </Typography>
                <Box
                    sx={{
                        width: '100%',
                        height: 50,
                        backgroundColor: settings[`${type}Color`],
                        mb: 2,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid #ccc'
                    }}
                    onClick={() => setShowColorPicker(prev => ({
                        ...prev,
                        [type]: !prev[type]
                    }))}
                />
                {showColorPicker[type] && (
                    <Box sx={{ position: 'absolute', zIndex: 2, right: 0 }}>
                        <Box
                            sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}
                            onClick={() => setShowColorPicker(prev => ({
                                ...prev,
                                [type]: false
                            }))}
                        />
                        <ChromePicker
                            color={settings[`${type}Color`]}
                            onChangeComplete={(color) => handleColorChange(color, `${type}Color`)}
                        />
                    </Box>
                )}
            </Paper>
        </Grid>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ fontFamily: "Cairo" }}>
                {t('SETTINGS.template_settings')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6" gutterBottom sx={{ fontFamily: "Cairo" }}>
                {t('SETTINGS.colors')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4, position: 'relative' }}>
                {['primary', 'secondary'].map(type => renderColorPicker(type))}
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom sx={{ fontFamily: "Cairo" }}>
                {t('SETTINGS.app_settings')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label={t('SETTINGS.app_name')}
                        value={settings.appName}
                        onChange={(e) => setSettings(prev => ({ ...prev, appName: e.target.value }))}
                        variant="outlined"
                        sx={{ fontFamily: "Cairo" }}
                    />
                </Grid>
            </Grid>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom sx={{ fontFamily: "Cairo" }}>
                {t('SETTINGS.images')}
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {['company_one', 'company_two', 'sidebar', 'main_icon', 'loading', 'icon'].map(type =>
                    renderImageUpload(t(`SETTINGS.${type}`), type)
                )}
            </Grid>



            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                <Button
                    variant="outlined"
                   
                    onClick={() => {
                        handleResetAll()
                    }}
                    sx={{
                        // flexGrow: 1,
                      
                       
                        borderColor: maintheme?.secondary,
                        color: maintheme?.secondary,
                        '&:hover': {
                            color: maintheme?.secondary + '88',
                            borderColor: maintheme?.secondary,
                        },
                        
                        fontFamily: 'Cairo-Bold'
                    }}
                >
                    {t('COMMON.reset_all')}
                </Button>
                <Button
                    variant="outlined"

                    onClick={handleApplyTheme}
                    startIcon={<CheckIcon />}
                    // spacing={2} 
                    sx={{
                        // flexGrow: 1,
                      
                       
                        borderColor: maintheme?.secondary,
                        color: maintheme?.secondary,
                        '&:hover': {
                            color: maintheme?.secondary + '88',
                            borderColor: maintheme?.secondary,
                        },
                        
                        fontFamily: 'Cairo-Bold'
                    }}
                >
                    {t('COMMON.apply')}
                </Button>
                <Button

                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    size="large"
                    variant="contained"
                    // spacing={2} 
                    sx={{
                        // flexGrow: 1,
                       
                       
                        backgroundColor: maintheme?.secondary,
                        '&:hover': {
                            backgroundColor: maintheme?.secondary + '88',
                        },
                      
                        fontFamily: 'Cairo-Bold'
                    }}
                >
                    {t('COMMON.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default TemplateSettings;